/**
 * Dashboard: close-progress tiles, KPI cards, the closing month by month
 * (with its per-month drill-down into uploads and account changes), daily
 * activity (runs vs uploads), tool usage, recent runs — all from /api/stats
 * and /api/stats/monthly, polled through the shared data layer (visibility
 * pause, abort on unmount, error surface with retry).
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { usePageTitle } from "../hooks/usePageTitle";
import { statsPollIntervalMs } from "../lib/states";
import { countByCategory, formatDateTime } from "../lib/runs";
import { ID_SLICE_LEN } from "../lib/config";
import { PageHeader } from "../components/ui/PageHeader";
import { MonthlyFigures } from "../components/MonthlyFigures";
import { resolveText } from "../lib/tooltext";
import { StateChip } from "../components/ui/StateChip";
import { DataTable } from "../components/ui/DataTable";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { EmptyState } from "../components/ui/EmptyState";

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  usePageTitle("dashboard.title");
  const navigate = useNavigate();
  // Mode-aware tokens and chart chrome: series colors, grid and tooltip
  // follow the active theme instead of hardcoded light-mode literals.
  const theme = useTheme();
  const chartColors = theme.tokens.chart;
  const tick = { fontSize: 11, fill: theme.palette.text.secondary };
  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  };
  const statsQuery = useApiQuery(["stats"], api.stats, {
    pollMs: statsPollIntervalMs(),
  });
  const stats = statsQuery.data ?? null;

  // tool widgets speak the operator's language, never the code name
  const toolUsage = useMemo(
    () =>
      (stats?.tool_usage ?? []).map((row) => ({
        ...row,
        label: resolveText(row.display_name, i18n.language) || row.tool,
      })),
    [stats, i18n.language],
  );

  const activity = useMemo(() => {
    if (!stats) return [];
    const merged = new Map<string, { date: string; runs: number; uploads: number }>();
    for (const r of stats.runs_daily) {
      merged.set(r.date, { date: r.date, runs: r.count, uploads: 0 });
    }
    for (const u of stats.uploads_daily) {
      const row = merged.get(u.date) ?? { date: u.date, runs: 0, uploads: 0 };
      row.uploads = u.count;
      merged.set(u.date, row);
    }
    return [...merged.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [stats]);

  // Loading, error and data are three different screens — an unreachable
  // backend must never render as an infinite spinner or a fake zero.
  if (!stats) {
    if (statsQuery.isError) {
      return (
        <ErrorAlert error={statsQuery.error} onRetry={() => void statsQuery.refetch()} />
      );
    }
    return <LinearProgress />;
  }

  const kpis = [
    { label: t("dashboard.totalRuns"), value: stats.totals.jobs },
    { label: t("dashboard.uploads"), value: stats.totals.uploads },
    { label: t("dashboard.outputs"), value: stats.totals.outputs },
    { label: t("dashboard.completed"), value: stats.totals.by_state.COMPLETED ?? 0 },
  ];

  // Close-progress tiles (Alteryx-style): where the work is RIGHT NOW,
  // each tile jumping to the surface that resolves it.
  const byCategory = countByCategory(stats.totals.by_state);
  const quickTiles = [
    {
      key: "running",
      label: t("dashboard.quickRunning"),
      value: byCategory.running,
      icon: <AutorenewIcon color="info" />,
      to: "/runs?cat=running",
    },
    {
      key: "waiting",
      label: t("dashboard.quickWaiting"),
      value: byCategory.waiting,
      icon: <HourglassTopIcon color="warning" />,
      to: "/approvals",
    },
    {
      key: "warning",
      label: t("dashboard.quickWarning"),
      value: byCategory.warning,
      icon: <WarningAmberIcon color="warning" />,
      to: "/runs?cat=warning",
    },
    {
      key: "failure",
      label: t("dashboard.quickFailure"),
      value: byCategory.failure,
      icon: <CancelIcon color="error" />,
      to: "/runs?cat=failure",
    },
  ];

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t("dashboard.title")}
        subtitle={t("dashboard.subtitle", { days: stats.window_days })}
      />

      <Grid container spacing={2}>
        {quickTiles.map((tile) => (
          <Grid item xs={6} md={3} key={tile.key}>
            <Card>
              <CardActionArea onClick={() => navigate(tile.to)}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {tile.icon}
                    <div>
                      <Typography variant="h5" className="num">
                        {tile.value.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tile.label}
                      </Typography>
                    </div>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {kpis.map((kpi) => (
          <Grid item xs={6} md={3} key={kpi.label}>
            <Card sx={{ borderTop: 3, borderTopColor: "secondary.main" }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {kpi.label}
                </Typography>
                <Typography variant="h4" className="num">
                  {kpi.value.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* The close itself comes first: the figures, then the month drill-down. */}
      <MonthlyFigures />

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("dashboard.activity")}
              </Typography>
              {activity.length === 0 ? (
                <EmptyState message={t("dashboard.empty")} />
              ) : (
                <Box role="img" aria-label={t("dashboard.activityChart")}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={activity}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                      <XAxis dataKey="date" tick={tick} tickFormatter={(d: string) => d.slice(5)} />
                      <YAxis allowDecimals={false} tick={tick} />
                      <ChartTooltip contentStyle={tooltipStyle} />
                      <Legend />
                      <Bar dataKey="runs" name={t("dashboard.runs")} fill={chartColors.runs} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="uploads" name={t("dashboard.uploadsSeries")} fill={chartColors.uploads} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("dashboard.toolUsage")}
              </Typography>
              {stats.tool_usage.length === 0 ? (
                <EmptyState message={t("dashboard.empty")} />
              ) : (
                <Box role="img" aria-label={t("dashboard.toolUsageChart")}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={toolUsage} layout="vertical" margin={{ left: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider} />
                      <XAxis type="number" allowDecimals={false} tick={tick} />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={200}
                        tick={tick}
                      />
                      <ChartTooltip contentStyle={tooltipStyle} />
                      <Bar dataKey="count" fill={chartColors.runs} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("dashboard.recentJobs")}
          </Typography>
          {stats.recent_jobs.length === 0 ? (
            <EmptyState message={t("dashboard.empty")} />
          ) : (
            <DataTable
              columns={[
                { key: "job", label: t("dashboard.job") },
                { key: "pipeline", label: t("dashboard.pipeline") },
                { key: "state", label: t("dashboard.state") },
                { key: "date", label: t("dashboard.date") },
              ]}
            >
              {stats.recent_jobs.map((job) => (
                <TableRow
                  key={job.job_id}
                  hover
                  onClick={() => navigate(`/runs/${job.job_id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell className="num">{job.job_id.slice(0, ID_SLICE_LEN)}</TableCell>
                  <TableCell>{job.dag}</TableCell>
                  <TableCell>
                    <StateChip state={job.state} />
                  </TableCell>
                  <TableCell className="num">
                    {formatDateTime(job.created_at, i18n.language)}
                  </TableCell>
                </TableRow>
              ))}
            </DataTable>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
